/**
 * Chat Session Service
 * Loads chat history from Firestore for v2 conversation persistence.
 * Server-side (Cloud Run) writes sessions and messages; this service reads them.
 *
 * Path: userContent/{uid}/chatSessions/{sessionId}/messages/{auto}
 */

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  getDocs,
  onSnapshot,
  Unsubscribe,
  type DocumentData,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ChatSession, ChatMessage } from '../types';

/** Members can resume any conversation from this window. */
export const CHAT_RESUME_WINDOW_DAYS = 30;

const USER_CONTENT_COLLECTION = 'userContent';
const CHAT_SESSIONS_SUBCOLLECTION = 'chatSessions';
const MESSAGES_SUBCOLLECTION = 'messages';

/**
 * Get the chat sessions collection reference for a user
 */
const getSessionsCollectionRef = (uid: string) =>
  collection(db, USER_CONTENT_COLLECTION, uid, CHAT_SESSIONS_SUBCOLLECTION);

/**
 * Get the messages collection reference for a specific session
 */
const getMessagesCollectionRef = (uid: string, sessionId: string) =>
  collection(
    db,
    USER_CONTENT_COLLECTION,
    uid,
    CHAT_SESSIONS_SUBCOLLECTION,
    sessionId,
    MESSAGES_SUBCOLLECTION
  );

/**
 * Get the most recent chat session for a user.
 * Returns null if no sessions exist.
 */
const toChatSession = (id: string, data: DocumentData): ChatSession => ({
  id,
  createdAt: data.createdAt ?? '',
  lastMessageAt: data.lastMessageAt ?? '',
  messageCount: data.messageCount ?? 0,
  transport: data.transport ?? 'unknown',
  lastUserMessage: typeof data.lastUserMessage === 'string' ? data.lastUserMessage : undefined,
  privacyLexicon:
    data.privacyLexicon && typeof data.privacyLexicon === 'object'
      ? (data.privacyLexicon as Record<string, string>)
      : undefined,
});

/**
 * Fetch one session document fresh. Used when resuming a conversation so the
 * privacy lexicon reflects writes that landed after the resume list was
 * cached (the server persists new tokens to the session doc mid-stream).
 */
export const getChatSession = async (
  uid: string,
  sessionId: string
): Promise<ChatSession | null> => {
  const ref = doc(db, USER_CONTENT_COLLECTION, uid, CHAT_SESSIONS_SUBCOLLECTION, sessionId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return toChatSession(snapshot.id, snapshot.data());
};

export const getLatestChatSession = async (uid: string): Promise<ChatSession | null> => {
  const q = query(
    getSessionsCollectionRef(uid),
    orderBy('lastMessageAt', 'desc'),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  return toChatSession(doc.id, doc.data());
};

/**
 * List sessions with activity in the last `sinceDays` days, newest first.
 * Powers the member "recent chats" resume list. lastMessageAt is stored as
 * an ISO-8601 UTC string, so string comparison orders correctly.
 */
export const listRecentChatSessions = async (
  uid: string,
  sinceDays: number = CHAT_RESUME_WINDOW_DAYS,
  max: number = 20,
): Promise<ChatSession[]> => {
  const cutoff = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000)
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z');
  const q = query(
    getSessionsCollectionRef(uid),
    where('lastMessageAt', '>=', cutoff),
    orderBy('lastMessageAt', 'desc'),
    limit(max)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((doc) => toChatSession(doc.id, doc.data()))
    .filter((s) => s.messageCount > 0);
};

/**
 * Load all messages from a chat session, ordered by timestamp.
 */
export const getChatSessionMessages = async (
  uid: string,
  sessionId: string
): Promise<ChatMessage[]> => {
  const q = query(
    getMessagesCollectionRef(uid, sessionId),
    orderBy('timestamp', 'asc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      role: data.role as 'user' | 'model',
      text: data.text ?? '',
      timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
    };
  });
};

/**
 * Subscribe to real-time message updates for a chat session.
 * Useful for seeing tool results appear live.
 */
export const subscribeToChatSessionMessages = (
  uid: string,
  sessionId: string,
  callback: (messages: ChatMessage[]) => void
): Unsubscribe => {
  const q = query(
    getMessagesCollectionRef(uid, sessionId),
    orderBy('timestamp', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const messages: ChatMessage[] = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        role: data.role as 'user' | 'model',
        text: data.text ?? '',
        timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
      };
    });
    callback(messages);
  });
};
